import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { AlertTriangle, Plus, Wrench, Shield, Clock, CheckCircle, RefreshCw, Eye, MessageSquare, MapPin, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INCIDENT_TYPES = [
  { value: 'SINISTRO', label: 'Sinistro/Danno', icon: AlertTriangle, color: 'text-red-600' },
  { value: 'GUASTO', label: 'Guasto/Malfunzionamento', icon: Wrench, color: 'text-orange-600' },
  { value: 'MANUTENZIONE', label: 'Richiesta Manutenzione', icon: Wrench, color: 'text-blue-600' },
  { value: 'SICUREZZA', label: 'Problema Sicurezza', icon: Shield, color: 'text-red-600' },
  { value: 'ALTRO', label: 'Altro', icon: MessageSquare, color: 'text-gray-600' }
];

const INCIDENT_STATUS = {
  NUOVO: { label: 'Nuovo', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  IN_LAVORAZIONE: { label: 'In Lavorazione', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  RISOLTO: { label: 'Risolto', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  CHIUSO: { label: 'Chiuso', color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
};

const PRIORITY_OPTIONS = [
  { value: 'CRITICAL', label: 'Critica', color: 'bg-red-500' },
  { value: 'HIGH', label: 'Alta', color: 'bg-orange-500' },
  { value: 'MEDIUM', label: 'Media', color: 'bg-yellow-500' },
  { value: 'LOW', label: 'Bassa', color: 'bg-blue-500' }
];

export const Incidents = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  const condoName = selectedCondominium?.condominium?.name || 'Condominio';
  
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  const [createForm, setCreateForm] = useState({
    type: 'GUASTO',
    title: '',
    description: '',
    location: '',
    priority: 'MEDIUM'
  });
  
  const [updateForm, setUpdateForm] = useState({
    status: '',
    notes: ''
  });

  const fetchIncidents = useCallback(async () => {
    if (!condoId) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/condominiums/${condoId}/incidents`;
      if (statusFilter) {
        url += `?status=${statusFilter}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIncidents(data.incidents || []);
      }
    } catch (error) {
      console.error('Error fetching incidents:', error);
    }
    setLoading(false);
  }, [condoId, token, statusFilter]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const handleCreateIncident = async () => {
    if (!createForm.title.trim() || !createForm.description.trim()) {
      toast.error('Compila titolo e descrizione');
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('type', createForm.type);
      formData.append('title', createForm.title);
      formData.append('description', createForm.description);
      formData.append('location', createForm.location);
      formData.append('priority', createForm.priority);
      
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/incidents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success('Segnalazione inviata');
        setCreateDialogOpen(false);
        setCreateForm({ type: 'GUASTO', title: '', description: '', location: '', priority: 'MEDIUM' });
        fetchIncidents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore invio segnalazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateIncident = async () => {
    if (!selectedIncident) return;
    
    setProcessing(true);
    try {
      const formData = new FormData();
      if (updateForm.status) formData.append('status', updateForm.status);
      if (updateForm.notes) formData.append('notes', updateForm.notes);
      
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/incidents/${selectedIncident.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success('Segnalazione aggiornata');
        setDetailDialogOpen(false);
        fetchIncidents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore aggiornamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const openDetail = (incident) => {
    setSelectedIncident(incident);
    setUpdateForm({
      status: incident.status,
      notes: incident.notes || ''
    });
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isAdmin = userRole === 'ADMIN';
  
  // Count by status
  const countByStatus = {
    NUOVO: incidents.filter(i => i.status === 'NUOVO').length,
    IN_LAVORAZIONE: incidents.filter(i => i.status === 'IN_LAVORAZIONE').length,
    RISOLTO: incidents.filter(i => i.status === 'RISOLTO').length,
    CHIUSO: incidents.filter(i => i.status === 'CHIUSO').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Segnalazioni"
        subtitle="Guasti, sinistri e interventi"
        actions={
          <Button className="bg-white text-primary hover:bg-white/90" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuova Segnalazione
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'NUOVO' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'NUOVO' ? '' : 'NUOVO')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Nuove</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{countByStatus.NUOVO}</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'IN_LAVORAZIONE' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'IN_LAVORAZIONE' ? '' : 'IN_LAVORAZIONE')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">In Lavorazione</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{countByStatus.IN_LAVORAZIONE}</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'RISOLTO' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'RISOLTO' ? '' : 'RISOLTO')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Risolte</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{countByStatus.RISOLTO}</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'CHIUSO' ? 'ring-2 ring-gray-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'CHIUSO' ? '' : 'CHIUSO')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Chiuse</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{countByStatus.CHIUSO}</div>
          </CardContent>
        </Card>
      </div>

      {/* Incidents Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista Segnalazioni</CardTitle>
              <CardDescription>
                {statusFilter ? `Filtro: ${INCIDENT_STATUS[statusFilter]?.label}` : 'Tutte le segnalazioni'}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchIncidents} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin" />
              Caricamento...
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna segnalazione trovata</p>
              <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Prima Segnalazione
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Titolo</TableHead>
                    <TableHead>Priorità</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Segnalato da</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.map((incident) => {
                    const typeConfig = INCIDENT_TYPES.find(t => t.value === incident.type) || INCIDENT_TYPES[4];
                    const TypeIcon = typeConfig.icon;
                    const statusConfig = INCIDENT_STATUS[incident.status] || INCIDENT_STATUS.NUOVO;
                    const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === incident.priority) || PRIORITY_OPTIONS[2];
                    
                    return (
                      <TableRow key={incident.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <TypeIcon className={`h-4 w-4 ${typeConfig.color}`} />
                            <span className="text-sm">{typeConfig.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{incident.title}</p>
                            {incident.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {incident.location}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${priorityConfig.color}`} />
                            <span className="text-sm">{priorityConfig.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <User className="h-3 w-3" />
                            {incident.reported_by_name || 'N/D'}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(incident.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetail(incident)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova Segnalazione</DialogTitle>
            <DialogDescription>
              Segnala un problema, guasto o richiesta di intervento
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Tipo Segnalazione</Label>
              <Select
                value={createForm.type}
                onValueChange={(v) => setCreateForm({...createForm, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INCIDENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className={`h-4 w-4 ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label>Titolo *</Label>
              <Input
                value={createForm.title}
                onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                placeholder="Breve descrizione del problema"
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Descrizione *</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                placeholder="Descrivi dettagliatamente il problema..."
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Ubicazione</Label>
                <Input
                  value={createForm.location}
                  onChange={(e) => setCreateForm({...createForm, location: e.target.value})}
                  placeholder="Es. Piano 3 - Scale A"
                />
              </div>
              <div className="grid gap-2">
                <Label>Priorità</Label>
                <Select
                  value={createForm.priority}
                  onValueChange={(v) => setCreateForm({...createForm, priority: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${p.color}`} />
                          {p.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateIncident} disabled={processing}>
              {processing && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Invia Segnalazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedIncident?.title}</DialogTitle>
            <DialogDescription>
              {INCIDENT_TYPES.find(t => t.value === selectedIncident?.type)?.label} • 
              Segnalato il {formatDate(selectedIncident?.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm">{selectedIncident.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Ubicazione</p>
                  <p className="font-medium">{selectedIncident.location || 'Non specificata'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Segnalato da</p>
                  <p className="font-medium">{selectedIncident.reported_by_name || 'N/D'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Priorità</p>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${PRIORITY_OPTIONS.find(p => p.value === selectedIncident.priority)?.color}`} />
                    {PRIORITY_OPTIONS.find(p => p.value === selectedIncident.priority)?.label}
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground">Stato Attuale</p>
                  <Badge className={INCIDENT_STATUS[selectedIncident.status]?.color}>
                    {INCIDENT_STATUS[selectedIncident.status]?.label}
                  </Badge>
                </div>
              </div>
              
              {selectedIncident.resolved_at && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Risolto il</p>
                  <p className="font-medium">{formatDate(selectedIncident.resolved_at)}</p>
                </div>
              )}
              
              {isAdmin && (
                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-medium">Aggiorna Segnalazione</h4>
                  
                  <div className="grid gap-2">
                    <Label>Nuovo Stato</Label>
                    <Select
                      value={updateForm.status}
                      onValueChange={(v) => setUpdateForm({...updateForm, status: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(INCIDENT_STATUS).map(([key, val]) => (
                          <SelectItem key={key} value={key}>
                            <Badge className={val.color}>{val.label}</Badge>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label>Note Admin</Label>
                    <Textarea
                      value={updateForm.notes}
                      onChange={(e) => setUpdateForm({...updateForm, notes: e.target.value})}
                      placeholder="Note interne sull'intervento..."
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Chiudi
            </Button>
            {isAdmin && (
              <Button onClick={handleUpdateIncident} disabled={processing}>
                {processing && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                Aggiorna
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Incidents;
